import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  addDoc,
  deleteDoc,
  serverTimestamp,
  DocumentData,
  limit,
} from "firebase/firestore"
import { db } from "./firebase"
import type { Team, User, Trip, ShareableLink, LeaderboardEntry, TripLeaderboardEntry, Group, GroupMember, GroupLeaderboardEntry } from "@/types"

// Interface for spot document data
interface SpotDocumentData {
  userId: string;
  teamId: string;
  mode?: string;
  location?: string;
  notes?: string;
  spottedAt: any; // Firestore Timestamp
}

// Collections
export const teamsCollection = collection(db, "teams")
export const usersCollection = collection(db, "users")
export const tripsCollection = collection(db, "trips")
export const tripSpotsCollection = collection(db, "trip_spots")
export const groupsCollection = collection(db, "groups")
export const groupMembersCollection = collection(db, "group_members")
export const userSpotsCollection = collection(db, "user_spots")
export const shareableLinksCollection = collection(db, "shareable_links")
export const tripParticipantsCollection = collection(db, "trip_participants")

// Team operations
export async function getAllTeams(): Promise<Team[]> {
  const snapshot = await getDocs(teamsCollection)
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as Team)
}

export async function getTeamById(teamId: string): Promise<Team | null> {
  const docRef = doc(teamsCollection, teamId)
  const snapshot = await getDoc(docRef)
  return snapshot.exists() ? ({ id: snapshot.id, ...snapshot.data() } as Team) : null
}

export async function searchTeams(searchTerm: string): Promise<Team[]> {
  const snapshot = await getDocs(teamsCollection)
  const teams = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as Team)

  return teams.filter(
    (team) =>
      team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      team.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (team.league?.toLowerCase() || "").includes(searchTerm.toLowerCase()),
  )
}

// User operations
export async function createUser(userData: Omit<User, "createdAt" | "lastActive">): Promise<void> {
  const userRef = doc(usersCollection, userData.id)
  await setDoc(userRef, {
    ...userData,
    createdAt: serverTimestamp(),
    lastActive: serverTimestamp(),
  })
}

export async function getUserById(userId: string): Promise<User | null> {
  const docRef = doc(usersCollection, userId)
  const snapshot = await getDoc(docRef)
  if (!snapshot.exists()) return null

  const data = snapshot.data()
  return {
    id: snapshot.id,
    ...data,
    createdAt: data.createdAt?.toDate() || new Date(),
    lastActive: data.lastActive?.toDate() || new Date(),
  } as User
}

// Alias for getUserById to match naming in UserTeamsPage
export async function getUserProfile(userId: string): Promise<User | null> {
  return getUserById(userId)
}

export async function updateUser(userId: string, updates: Partial<User>): Promise<void> {
  const userRef = doc(usersCollection, userId)
  await updateDoc(userRef, {
    ...updates,
    lastActive: serverTimestamp(),
  })
}

// Spot a team for a user
export async function spotTeam(
  userId: string, 
  teamId: string, 
  location?: string, 
  notes?: string,
  mode: 'all-time' | 'trip-mode' = 'all-time'
): Promise<void> {
  // Create base document data
  const docData: {
    userId: string;
    teamId: string;
    spottedAt: any;
    mode: string;
    location?: string;
    notes?: string;
  } = {
    userId,
    teamId,
    spottedAt: serverTimestamp(),
    mode, // Track which mode this spot belongs to
  }
  
  // Only add location and notes if they're defined
  if (location) docData.location = location;
  if (notes) docData.notes = notes;
  
  // Add to user_spots collection
  await addDoc(userSpotsCollection, docData)

  // Update user's spotted teams and total count
  // For all-time mode only, we update the user document
  if (mode === 'all-time') {
    let user = await getUserById(userId)
    
    // If user doesn't exist in Firestore, create them
    if (!user) {
      console.log('User not found in Firestore, creating new user record for:', userId)
      await createUser({
        id: userId,
        email: "unknown@example.com", // This should be updated with real email from Clerk
        displayName: "User",
        spottedTeams: [],
        totalSpots: 0,
        achievements: [],
      })
      user = await getUserById(userId)
    }
    
    if (user) {
      const spottedTeams = user.spottedTeams?.includes(teamId) ? user.spottedTeams : [...(user.spottedTeams || []), teamId]

      await updateUser(userId, {
        spottedTeams,
        totalSpots: (user.totalSpots || 0) + 1,
      })
    }
  }
}

// Load teams from JSON files (same as useTeams hook)
async function loadTeamsFromJSON(): Promise<Team[]> {
  try {
    // Fetch all JSON files in parallel for maximum speed
    const [nflRes, mlbRes, nbaRes, nhlRes, mlsRes, nationalRes, soccerRes, nwslRes, wnbaRes] = await Promise.all([
      fetch('/enhanced_nfl_teams.json'),
      fetch('/enhanced_mlb_teams.json'), 
      fetch('/enhanced_nba_teams.json'),
      fetch('/enhanced_nhl_teams.json'),
      fetch('/enhanced_mls_teams.json'),
      fetch('/enhanced_national_teams.json'),
      fetch('/enhanced_top_tier_soccer_leagues.json'),
      fetch('/enhanced_nwsl_teams.json'),
      fetch('/enhanced_wnba_teams.json')
    ])

    // Parse all JSON responses in parallel
    const [nflData, mlbData, nbaData, nhlData, mlsData, nationalData, soccerData, nwslData, wnbaData] = await Promise.all([
      nflRes.json(),
      mlbRes.json(),
      nbaRes.json(), 
      nhlRes.json(),
      mlsRes.json(),
      nationalRes.json(),
      soccerRes.json(),
      nwslRes.json(),
      wnbaRes.json()
    ])

    // Map data to consistent Team format
    const allTeams: Team[] = [
      ...mapNFLTeams(nflData),
      ...mapMLBTeams(mlbData),
      ...mapNBATeams(nbaData),
      ...mapNHLTeams(nhlData),
      ...mapMLSTeams(mlsData),
      ...mapNationalTeams(nationalData),
      ...mapSoccerLeagues(soccerData),
      ...mapNWSLTeams(nwslData),
      ...mapWNBATeams(wnbaData)
    ]

    return allTeams
  } catch (error) {
    console.error('Error loading teams from JSON:', error)
    return []
  }
}

// Team mapping functions (duplicated from useTeams)
function mapNFLTeams(data: any[]): Team[] {
  return data.map(team => ({
    id: team.id,
    name: team.name,
    city: team.city,
    abbreviation: team.abbreviation,
    league: "NFL",
    sport: "Football", 
    conference: team.conference,
    division: team.division,
    founded: team.founded || 1920,
    information: team.information,
    state: team.state || "",
    country: "USA",
    primaryColor: team.primary_color || "#013369",
    secondaryColor: team.secondary_color || "#D50A0A",
  }))
}

function mapMLBTeams(data: any[]): Team[] {
  return data.map(team => ({
    id: team.id,
    name: team.name,
    city: team.city || team.locationName,
    abbreviation: team.abbreviation,
    league: "MLB",
    sport: "Baseball",
    conference: team.league || "",
    division: team.division || "", 
    founded: team.founded || (team.firstYearOfPlay ? Number(team.firstYearOfPlay) : 1900),
    information: team.information,
    state: team.state || "",
    country: team.country || "USA",
    primaryColor: team.primary_color || "#002D72",
    secondaryColor: team.secondary_color || "#E4002C",
  }))
}

function mapNBATeams(data: any[]): Team[] {
  return data.map(team => ({
    id: team.id,
    name: team.name,
    city: team.city,
    abbreviation: team.abbreviation,
    league: "NBA", 
    sport: "Basketball",
    conference: team.conference,
    division: team.division,
    founded: team.founded || 1950,
    information: team.information,
    state: team.state || "",
    country: team.country || "USA",
    primaryColor: team.primary_color || "#17408B",
    secondaryColor: team.secondary_color || "#C9082A",
  }))
}

function mapNHLTeams(data: any[]): Team[] {
  return data.map(team => ({
    id: team.id,
    name: team.name,
    city: team.city,
    abbreviation: team.abbreviation,
    league: "NHL",
    sport: "Hockey",
    conference: team.conference,
    division: team.division, 
    founded: team.founded || 1917,
    information: team.information,
    state: team.state || "",
    country: team.country || "USA",
    primaryColor: team.primary_color || "#000000",
    secondaryColor: team.secondary_color || "#FFFFFF",
  }))
}

function mapMLSTeams(data: any[]): Team[] {
  return data.map(team => ({
    id: team.id,
    name: team.name,
    city: team.city,
    abbreviation: team.abbreviation,
    league: "MLS",
    sport: "Soccer",
    conference: team.conference,
    division: team.division || "",
    founded: team.founded || 1996,
    information: team.information,
    state: team.state || "",
    country: team.country || "USA", 
    primaryColor: team.primary_color || "#1D4289",
    secondaryColor: team.secondary_color || "#52B2E8",
  }))
}

function mapNationalTeams(data: any): Team[] {
  if (data.uefa) {
    return data.uefa.map((team: any) => ({
      id: team.id,
      name: team.name,
      city: team.name,
      abbreviation: team.abbreviation,
      league: "UEFA",
      sport: "Soccer",
      conference: "UEFA",
      division: "",
      founded: 1954,
      information: team.information,
      state: "",
      country: team.name,
      primaryColor: team.primary_color || "#003366",
      secondaryColor: team.secondary_color || "#FFD700",
    }))
  }
  return []
}

function mapSoccerLeagues(data: any): Team[] {
  const allTeams: Team[] = []
  
  // Handle array structure where each league is an object with teams array
  if (Array.isArray(data)) {
    data.forEach((league: any) => {
      if (league.teams && Array.isArray(league.teams)) {
        const teams = league.teams.map((team: any) => ({
          id: team.id,
          name: team.name,
          city: team.city,
          abbreviation: team.abbreviation,
          league: league.league || "Unknown League",
          sport: "Soccer",
          conference: league.country || "",
          division: "",
          founded: team.founded || 1900,
          information: team.information,
          state: "",
          country: league.country || "Unknown",
          primaryColor: team.primary_color || "#37003C",
          secondaryColor: team.secondary_color || "#00FF87",
        }))
        allTeams.push(...teams)
      }
    })
  }
  
  return allTeams
}

function mapNWSLTeams(data: any): Team[] {
  // Handle both array and object with teams array
  const teams = Array.isArray(data) ? data : (data.teams || [])
  return teams.map((team: any) => ({
    id: team.id,
    name: team.name,
    city: team.city,
    abbreviation: team.abbreviation,
    league: "NWSL",
    sport: "Soccer",
    conference: team.conference || "",
    division: team.division || "",
    founded: team.founded || 2013,
    information: team.information,
    state: team.state || "",
    country: team.country || "USA",
    primaryColor: team.primary_color || "#1D4289",
    secondaryColor: team.secondary_color || "#52B2E8",
  }))
}

function mapWNBATeams(data: any): Team[] {
  // Handle both array and object with teams array
  const teams = Array.isArray(data) ? data : (data.teams || [])
  return teams.map((team: any) => ({
    id: team.id,
    name: team.name,
    city: team.city,
    abbreviation: team.abbreviation,
    league: "WNBA",
    sport: "Basketball",
    conference: team.conference || "",
    division: team.division || "",
    founded: team.founded || 1997,
    information: team.information,
    state: team.state || "",
    country: team.country || "USA",
    primaryColor: team.primary_color || "#FF6900",
    secondaryColor: team.secondary_color || "#FFFFFF",
  }))
}

// Get user's spotted teams with details
export async function getUserSpottedTeams(
  userId: string,
  mode: 'all-time' | 'trip-mode' = 'all-time'
): Promise<(Team & { spottedAt: Date; mode?: string })[]> {
  console.log('getUserSpottedTeams called with userId:', userId, 'mode:', mode)
  
  // Query spots by userId and mode
  let spotsQuery;
  if (mode === 'all-time') {
    // For all-time, we need to get spots that are either explicitly 'all-time' or don't have a mode set (for backward compatibility)
    // Since Firestore doesn't handle null values well in 'in' queries, we'll fetch all user spots and filter client-side
    spotsQuery = query(userSpotsCollection, where("userId", "==", userId))
  } else {
    // For trip-mode, only get spots explicitly marked as 'trip-mode'
    spotsQuery = query(
      userSpotsCollection, 
      where("userId", "==", userId),
      where("mode", "==", "trip-mode")
    )
  }

  console.log('Executing Firestore query...')
  const spotsSnapshot = await getDocs(spotsQuery)
  console.log('Query returned', spotsSnapshot.docs.length, 'spot documents')
  let spots = spotsSnapshot.docs.map((doc) => {
    const data = doc.data() as SpotDocumentData
    console.log('Processing spot document:', doc.id, 'data:', data)
    return {
      id: doc.id,
      userId: data.userId,
      teamId: data.teamId,
      mode: data.mode,
      location: data.location,
      notes: data.notes,
      spottedAt: data.spottedAt?.toDate() || new Date(),
    }
  })
  
  // Client-side filtering for all-time mode to exclude trip-mode spots
  if (mode === 'all-time') {
    spots = spots.filter(spot => {
      const spotMode = spot.mode
      const isAllTimeSpot = !spotMode || spotMode === 'all-time'
      console.log('Filtering spot:', spot.teamId, 'mode:', spotMode, 'isAllTimeSpot:', isAllTimeSpot)
      return isAllTimeSpot
    })
    console.log('After filtering for all-time mode:', spots.length, 'spots remain')
  }
  
  console.log('Processed spots:', spots)
  
  // Sort by spottedAt descending (client-side sorting until indexes are built)
  spots.sort((a, b) => b.spottedAt.getTime() - a.spottedAt.getTime())

  // Get unique team IDs
  const uniqueTeamIds = [...new Set(spots.map((spot) => spot.teamId))]
  console.log('Unique team IDs to fetch:', uniqueTeamIds)

  // Load teams from JSON files
  console.log('Loading team details from JSON files...')
  const allTeams = await loadTeamsFromJSON()
  console.log('Loaded', allTeams.length, 'teams from JSON')
  
  // Create a map for quick lookup
  const teamMap = new Map(allTeams.map(team => [team.id, team]))
  
  // Combine team data with spot data - use unique team IDs to avoid duplicates
  const spottedTeamsWithDetails = uniqueTeamIds
    .map(teamId => {
      const team = teamMap.get(teamId)
      if (!team) {
        console.warn('Team not found for ID:', teamId)
        return null
      }
      
      // Find the most recent spot for this team
      const teamSpots = spots.filter(spot => spot.teamId === teamId)
      const mostRecentSpot = teamSpots.sort((a, b) => b.spottedAt.getTime() - a.spottedAt.getTime())[0]
      
      return {
        ...team,
        spottedAt: mostRecentSpot.spottedAt,
        mode: mostRecentSpot.mode
      }
    })
    .filter(Boolean) as (Team & { spottedAt: Date; mode?: string })[]
  
  console.log('Returning', spottedTeamsWithDetails.length, 'spotted teams with details')
  return spottedTeamsWithDetails
}

// Remove a specific team spot for a user
export async function removeTeamSpot(
  userId: string, 
  teamId: string, 
  mode: 'all-time' | 'trip-mode' = 'all-time'
): Promise<void> {
  try {
    // Find the specific spot to remove
    const spotsQuery = query(
      userSpotsCollection,
      where("userId", "==", userId),
      where("teamId", "==", teamId),
      where("mode", "==", mode)
    )

    const spotsSnapshot = await getDocs(spotsQuery)
    
    if (spotsSnapshot.docs.length === 0) {
      console.warn('No spots found to delete for:', { userId, teamId, mode })
      return
    }
    
    // Delete the spot documents (there might be multiple if spotted multiple times)
    const deletePromises = spotsSnapshot.docs.map(async (docSnap) => {
      await deleteDoc(doc(userSpotsCollection, docSnap.id))
    })
    
    await Promise.all(deletePromises)
    console.log('Successfully deleted', spotsSnapshot.docs.length, 'spot document(s) for team:', teamId)

    // Update user's spotted teams count for all-time mode
    if (mode === 'all-time') {
      const user = await getUserById(userId)
      if (user) {
        const spottedTeams = user.spottedTeams?.filter(id => id !== teamId) || []
        await updateUser(userId, {
          spottedTeams,
          totalSpots: Math.max((user.totalSpots || 0) - 1, 0),
        })
      }
    }
  } catch (error) {
    console.error('Error in removeTeamSpot:', error)
    throw error
  }
}

// Reset trip mode spots for a user
export async function resetTripModeSpots(userId: string): Promise<void> {
  // Find all trip mode spots for the user
  const spotsQuery = query(
    userSpotsCollection,
    where("userId", "==", userId),
    where("mode", "==", "trip-mode")
  )

  const spotsSnapshot = await getDocs(spotsQuery)
  
  // Delete each spot document
  const deletePromises = spotsSnapshot.docs.map(async (docSnap) => {
    await deleteDoc(doc(userSpotsCollection, docSnap.id))
  })
  
  await Promise.all(deletePromises)
}

// Trip operations
export async function createTrip(tripData: Omit<Trip, "id" | "createdAt" | "joinCode">): Promise<string> {
  const joinCode = Math.random().toString(36).substring(2, 8).toUpperCase()

  const docRef = await addDoc(tripsCollection, {
    ...tripData,
    joinCode,
    createdAt: serverTimestamp(),
  })

  return docRef.id
}

export async function getTripById(tripId: string): Promise<Trip | null> {
  const docRef = doc(tripsCollection, tripId)
  const snapshot = await getDoc(docRef)
  if (!snapshot.exists()) return null

  const data = snapshot.data()
  return {
    id: snapshot.id,
    ...data,
    createdAt: data.createdAt?.toDate() || new Date(),
    startsAt: data.startsAt?.toDate() || new Date(),
    endsAt: data.endsAt?.toDate() || new Date(),
  } as Trip
}

export async function joinTrip(tripId: string, userId: string): Promise<void> {
  const tripRef = doc(tripsCollection, tripId)
  const trip = await getTripById(tripId)

  if (trip && !trip.participants.includes(userId)) {
    await updateDoc(tripRef, {
      participants: [...trip.participants, userId],
    })
  }
}

// Enhanced Trip Operations with Competition Features

// Generate a unique share token
function generateShareToken(): string {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
}

// Create a new trip with sharing capabilities
export async function createCompetitionTrip(
  tripData: Omit<Trip, "id" | "createdAt" | "joinCode" | "shareToken">
): Promise<string> {
  const joinCode = Math.random().toString(36).substring(2, 8).toUpperCase()
  const shareToken = generateShareToken()

  const docRef = await addDoc(tripsCollection, {
    ...tripData,
    joinCode,
    shareToken,
    createdAt: serverTimestamp(),
    isActive: true,
    settings: {
      allowLateJoins: true,
      trackLocation: false,
      ...tripData.settings
    }
  })

  // Add creator as first participant
  await addDoc(tripParticipantsCollection, {
    tripId: docRef.id,
    userId: tripData.creatorId,
    userName: tripData.creatorName || 'Creator',
    joinedAt: serverTimestamp(),
    isCreator: true,
    isActive: true
  })

  return docRef.id
}

// Get trip by share token
export async function getTripByShareToken(shareToken: string): Promise<Trip | null> {
  const q = query(tripsCollection, where("shareToken", "==", shareToken))
  const snapshot = await getDocs(q)
  
  if (snapshot.empty) return null
  
  const doc = snapshot.docs[0]
  const data = doc.data()
  return {
    id: doc.id,
    ...data,
    createdAt: data.createdAt?.toDate() || new Date(),
    startsAt: data.startsAt?.toDate(),
    endsAt: data.endsAt?.toDate(),
  } as Trip
}

// Join trip via share token
export async function joinTripByShareToken(
  shareToken: string, 
  userId: string, 
  userName: string
): Promise<{ success: boolean; tripId?: string; error?: string }> {
  try {
    const trip = await getTripByShareToken(shareToken)
    
    if (!trip) {
      return { success: false, error: "Trip not found" }
    }

    if (!trip.isActive) {
      return { success: false, error: "Trip is no longer active" }
    }

    // Check if user is already a participant
    const participantQuery = query(
      tripParticipantsCollection,
      where("tripId", "==", trip.id),
      where("userId", "==", userId)
    )
    const existingParticipant = await getDocs(participantQuery)
    
    if (!existingParticipant.empty) {
      return { success: false, error: "You are already a participant in this trip" }
    }

    // Add user as participant
    await addDoc(tripParticipantsCollection, {
      tripId: trip.id,
      userId,
      userName,
      joinedAt: serverTimestamp(),
      isCreator: false,
      isActive: true
    })

    // Update trip participants array for backward compatibility
    await updateDoc(doc(tripsCollection, trip.id), {
      participants: [...trip.participants, userId]
    })

    return { success: true, tripId: trip.id }
  } catch (error) {
    console.error('Error joining trip:', error)
    return { success: false, error: "Failed to join trip" }
  }
}

// Get trip participants with detailed information
export async function getTripParticipants(tripId: string): Promise<TripLeaderboardEntry[]> {
  try {
    const participantsQuery = query(
      tripParticipantsCollection,
      where("tripId", "==", tripId),
      where("isActive", "==", true)
    )
    const participantsSnapshot = await getDocs(participantsQuery)
    
    const participants = await Promise.all(
      participantsSnapshot.docs.map(async (participantDoc) => {
        const participantData = participantDoc.data()
        
        // Get user's spots for this trip (in trip-mode)
        const userSpots = await getUserSpottedTeams(participantData.userId, 'trip-mode')
        const totalSpots = userSpots.length
        const uniqueLeagues = new Set(userSpots.map(spot => spot.league)).size
        const lastSpottedAt = userSpots.length > 0 ? userSpots[0].spottedAt : undefined

        return {
          userId: participantData.userId,
          userName: participantData.userName,
          totalSpots,
          uniqueLeagues,
          lastSpottedAt,
          rank: 0, // Will be calculated after sorting
          progress: 0, // Will be calculated after sorting
          tripId,
          joinedAt: participantData.joinedAt?.toDate() || new Date(),
          isCreator: participantData.isCreator || false
        } as TripLeaderboardEntry
      })
    )

    // Sort by total spots (descending), then by unique leagues (descending)
    participants.sort((a, b) => {
      if (b.totalSpots !== a.totalSpots) {
        return b.totalSpots - a.totalSpots
      }
      return b.uniqueLeagues - a.uniqueLeagues
    })

    // Calculate ranks and progress
    const maxSpots = participants.length > 0 ? participants[0].totalSpots : 0
    participants.forEach((participant, index) => {
      participant.rank = index + 1
      participant.progress = maxSpots > 0 ? Math.round((participant.totalSpots / maxSpots) * 100) : 0
    })

    return participants
  } catch (error) {
    console.error('Error getting trip participants:', error)
    return []
  }
}

// Get all-time leaderboard
export async function getAllTimeLeaderboard(limit_count: number = 10): Promise<LeaderboardEntry[]> {
  try {
    // Get all users with their spot counts
    const usersSnapshot = await getDocs(usersCollection)
    
    const leaderboard = await Promise.all(
      usersSnapshot.docs.map(async (userDoc) => {
        const userData = userDoc.data()
        
        // Get user's all-time spots
        const userSpots = await getUserSpottedTeams(userData.id, 'all-time')
        const totalSpots = userSpots.length
        const uniqueLeagues = new Set(userSpots.map(spot => spot.league)).size
        const lastSpottedAt = userSpots.length > 0 ? userSpots[0].spottedAt : undefined

        return {
          userId: userData.id,
          userName: userData.displayName || 'Anonymous',
          userEmail: userData.email,
          profileImageUrl: userData.profileImageUrl,
          totalSpots,
          uniqueLeagues,
          lastSpottedAt,
          rank: 0, // Will be calculated after sorting
          progress: 0 // Will be calculated after sorting
        } as LeaderboardEntry
      })
    )

    // Filter out users with no spots and sort by total spots
    const filteredLeaderboard = leaderboard
      .filter(entry => entry.totalSpots > 0)
      .sort((a, b) => {
        if (b.totalSpots !== a.totalSpots) {
          return b.totalSpots - a.totalSpots
        }
        return b.uniqueLeagues - a.uniqueLeagues
      })
      .slice(0, limit_count)

    // Calculate ranks and progress
    const maxSpots = filteredLeaderboard.length > 0 ? filteredLeaderboard[0].totalSpots : 0
    filteredLeaderboard.forEach((entry, index) => {
      entry.rank = index + 1
      entry.progress = maxSpots > 0 ? Math.round((entry.totalSpots / maxSpots) * 100) : 0
    })

    return filteredLeaderboard
  } catch (error) {
    console.error('Error getting all-time leaderboard:', error)
    return []
  }
}

// Shareable Links Operations

// Create a shareable link
export async function createShareableLink(
  type: 'all-time' | 'trip' | 'group',
  createdBy: string,
  targetId?: string,
  expiresInDays?: number
): Promise<string> {
  const shareToken = generateShareToken()
  const expiresAt = expiresInDays ? 
    new Date(Date.now() + expiresInDays * 24 * 60 * 60 * 1000) : 
    undefined

  const docData: any = {
    type,
    shareToken,
    createdBy,
    createdAt: serverTimestamp(),
    expiresAt: expiresAt ? expiresAt : null,
    isActive: true,
    viewCount: 0
  }

  if (targetId) {
    docData.targetId = targetId
  }

  await addDoc(shareableLinksCollection, docData)

  return shareToken
}

// Get shareable link data
export async function getShareableLinkData(shareToken: string): Promise<ShareableLink | null> {
  try {
    const q = query(shareableLinksCollection, where("shareToken", "==", shareToken))
    const snapshot = await getDocs(q)
    
    if (snapshot.empty) return null
    
    const linkDoc = snapshot.docs[0]
    const data = linkDoc.data()
    
    // Check if expired
    if (data.expiresAt && data.expiresAt.toDate() < new Date()) {
      return null
    }

    // Increment view count
    await updateDoc(linkDoc.ref, {
      viewCount: (data.viewCount || 0) + 1,
      lastViewedAt: serverTimestamp()
    })

    return {
      id: linkDoc.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      expiresAt: data.expiresAt?.toDate(),
      lastViewedAt: data.lastViewedAt?.toDate()
    } as ShareableLink
  } catch (error) {
    console.error('Error getting shareable link data:', error)
    return null
  }
}

// Get shared user progress (for all-time sharing)
export async function getSharedUserProgress(userId: string): Promise<{
  user: User | null;
  spottedTeams: (Team & { spottedAt: Date })[];
  stats: {
    totalSpots: number;
    uniqueLeagues: number;
    totalTeams: number;
    progress: number;
  }
}> {
  try {
    const user = await getUserById(userId)
    if (!user) {
      return {
        user: null,
        spottedTeams: [],
        stats: { totalSpots: 0, uniqueLeagues: 0, totalTeams: 0, progress: 0 }
      }
    }

    const spottedTeams = await getUserSpottedTeams(userId, 'all-time')
    const allTeams = await loadTeamsFromJSON()
    
    const stats = {
      totalSpots: spottedTeams.length,
      uniqueLeagues: new Set(spottedTeams.map(team => team.league)).size,
      totalTeams: allTeams.length,
      progress: allTeams.length > 0 ? Math.round((spottedTeams.length / allTeams.length) * 100) : 0
    }

    return { user, spottedTeams, stats }
  } catch (error) {
    console.error('Error getting shared user progress:', error)
    return {
      user: null,
      spottedTeams: [],
      stats: { totalSpots: 0, uniqueLeagues: 0, totalTeams: 0, progress: 0 }
    }
  }
}

// Update trip name and settings
export async function updateTrip(
  tripId: string, 
  updates: Partial<Omit<Trip, 'id' | 'createdAt' | 'shareToken' | 'joinCode'>>
): Promise<void> {
  const tripRef = doc(tripsCollection, tripId)
  await updateDoc(tripRef, updates)
}

// Delete trip (only by creator)
export async function deleteTrip(tripId: string, userId: string): Promise<boolean> {
  try {
    const trip = await getTripById(tripId)
    if (!trip || trip.creatorId !== userId) {
      return false
    }

    // Delete trip
    await deleteDoc(doc(tripsCollection, tripId))
    
    // Delete associated participants
    const participantsQuery = query(tripParticipantsCollection, where("tripId", "==", tripId))
    const participantsSnapshot = await getDocs(participantsQuery)
    
    const deletePromises = participantsSnapshot.docs.map(doc => deleteDoc(doc.ref))
    await Promise.all(deletePromises)

    return true
  } catch (error) {
    console.error('Error deleting trip:', error)
    return false
  }
}

// Reset trip progress (only by creator)
export async function resetTripProgress(tripId: string, userId: string): Promise<boolean> {
  try {
    const trip = await getTripById(tripId)
    if (!trip || trip.creatorId !== userId) {
      return false
    }

    // Get all participants
    const participants = await getTripParticipants(tripId)
    
    // Reset trip-mode spots for all participants
    const resetPromises = participants.map(participant => 
      resetTripModeSpots(participant.userId)
    )
    
    await Promise.all(resetPromises)
    return true
  } catch (error) {
    console.error('Error resetting trip progress:', error)
    return false
  }
}

// Get user's active trips
export async function getUserTrips(userId: string): Promise<Trip[]> {
  try {
    const participantsQuery = query(
      tripParticipantsCollection,
      where("userId", "==", userId),
      where("isActive", "==", true)
    )
    const participantsSnapshot = await getDocs(participantsQuery)
    
    const trips = await Promise.all(
      participantsSnapshot.docs.map(async (participantDoc) => {
        const participantData = participantDoc.data()
        return await getTripById(participantData.tripId)
      })
    )

    return trips.filter(trip => trip !== null) as Trip[]
  } catch (error) {
    console.error('Error getting user trips:', error)
    return []
  }
}

// Group Functions
export async function createGroup(
  name: string,
  description: string | undefined,
  createdBy: string,
  creatorName: string,
  settings: {
    isPublic: boolean
    allowNewMembers: boolean
    leaderboardType: 'all-time' | 'since-join'
  }
): Promise<string> {
  const shareToken = generateShareToken()
  
  const groupData: any = {
    name,
    createdBy,
    createdAt: serverTimestamp(),
    shareToken,
    isActive: true,
    settings,
    memberCount: 1,
    lastActivity: serverTimestamp()
  }

  if (description) {
    groupData.description = description
  }

  const groupDoc = await addDoc(groupsCollection, groupData)
  
  // Add creator as first member
  await addDoc(groupMembersCollection, {
    groupId: groupDoc.id,
    userId: createdBy,
    userName: creatorName,
    joinedAt: serverTimestamp(),
    isCreator: true,
    isActive: true
  })
  
  return groupDoc.id
}

export async function getGroupByShareToken(shareToken: string): Promise<Group | null> {
  try {
    const groupsQuery = query(
      groupsCollection,
      where("shareToken", "==", shareToken),
      where("isActive", "==", true)
    )
    const groupsSnapshot = await getDocs(groupsQuery)
    
    if (groupsSnapshot.empty) return null
    
    const groupDoc = groupsSnapshot.docs[0]
    const groupData = groupDoc.data()
    
    return {
      id: groupDoc.id,
      name: groupData.name,
      description: groupData.description,
      createdBy: groupData.createdBy,
      createdAt: groupData.createdAt.toDate(),
      shareToken: groupData.shareToken,
      isActive: groupData.isActive,
      settings: groupData.settings,
      memberCount: groupData.memberCount,
      lastActivity: groupData.lastActivity.toDate()
    }
  } catch (error) {
    console.error('Error getting group by share token:', error)
    return null
  }
}

export async function joinGroup(
  shareToken: string,
  userId: string,
  userName: string
): Promise<{success: boolean; groupId?: string; error?: string}> {
  try {
    const group = await getGroupByShareToken(shareToken)
    if (!group) {
      return { success: false, error: 'Group not found' }
    }
    
    if (!group.settings.allowNewMembers) {
      return { success: false, error: 'Group is not accepting new members' }
    }
    
    // Check if user is already a member
    const existingMemberQuery = query(
      groupMembersCollection,
      where("groupId", "==", group.id),
      where("userId", "==", userId)
    )
    const existingMemberSnapshot = await getDocs(existingMemberQuery)
    
    if (!existingMemberSnapshot.empty) {
      return { success: true, groupId: group.id } // Already a member
    }
    
    // Add user to group
    await addDoc(groupMembersCollection, {
      groupId: group.id,
      userId,
      userName,
      joinedAt: serverTimestamp(),
      isCreator: false,
      isActive: true
    })
    
    // Update group member count
    const groupRef = doc(groupsCollection, group.id)
    await updateDoc(groupRef, {
      memberCount: group.memberCount + 1,
      lastActivity: serverTimestamp()
    })
    
    return { success: true, groupId: group.id }
  } catch (error) {
    console.error('Error joining group:', error)
    return { success: false, error: 'Failed to join group' }
  }
}

export async function getGroupMembers(groupId: string): Promise<GroupMember[]> {
  try {
    const membersQuery = query(
      groupMembersCollection,
      where("groupId", "==", groupId),
      where("isActive", "==", true)
    )
    const membersSnapshot = await getDocs(membersQuery)
    
    return membersSnapshot.docs.map(doc => {
      const data = doc.data()
      return {
        id: doc.id,
        groupId: data.groupId,
        userId: data.userId,
        userName: data.userName,
        joinedAt: data.joinedAt.toDate(),
        isCreator: data.isCreator,
        isActive: data.isActive,
        invitedBy: data.invitedBy
      }
    })
  } catch (error) {
    console.error('Error getting group members:', error)
    return []
  }
}

export async function getGroupLeaderboard(groupId: string): Promise<GroupLeaderboardEntry[]> {
  try {
    const members = await getGroupMembers(groupId)
    
    const leaderboard = await Promise.all(
      members.map(async (member) => {
        const userSpots = await getUserSpottedTeams(member.userId, 'all-time')
        const spotsInGroup = userSpots.filter(spot => 
          spot.spottedAt >= member.joinedAt
        ).length
        
        const uniqueLeagues = new Set(userSpots.map(spot => spot.league)).size
        const progress = userSpots.length > 0 ? Math.round((userSpots.length / 150) * 100) : 0 // Assuming 150 total teams

        const userDoc = await getDoc(doc(usersCollection, member.userId))
        const userData = userDoc.exists() ? userDoc.data() : null

        return {
          userId: member.userId,
          userName: member.userName,
          profileImageUrl: userData?.profileImageUrl,
          totalSpots: userSpots.length,
          uniqueLeagues,
          progress,
          rank: 0, // Will be calculated after sorting
          groupId: member.groupId,
          joinedAt: member.joinedAt,
          isCreator: member.isCreator,
          spotsInGroup
        }
      })
    )
    
    // Sort by total spots (descending) and assign ranks
    leaderboard.sort((a, b) => b.totalSpots - a.totalSpots)
    leaderboard.forEach((entry, index) => {
      entry.rank = index + 1
    })
    
    return leaderboard
  } catch (error) {
    console.error('Error getting group leaderboard:', error)
    return []
  }
}

export async function getGroupShareData(shareToken: string): Promise<{
  group: Group | null
  members: GroupMember[]
  leaderboard: GroupLeaderboardEntry[]
}> {
  try {
    const group = await getGroupByShareToken(shareToken)
    if (!group) {
      return { group: null, members: [], leaderboard: [] }
    }
    
    const members = await getGroupMembers(group.id)
    const leaderboard = await getGroupLeaderboard(group.id)
    
    return { group, members, leaderboard }
  } catch (error) {
    console.error('Error getting group share data:', error)
    return { group: null, members: [], leaderboard: [] }
  }
}

// Sync user profile from Clerk to Firestore
export async function syncUserProfile(clerkUser: any): Promise<void> {
  if (!clerkUser?.id) return

  try {
    const userRef = doc(usersCollection, clerkUser.id)
    const userSnap = await getDoc(userRef)
    
    const profileData = {
      displayName: clerkUser.unsafeMetadata?.displayName || clerkUser.fullName || clerkUser.firstName || 'User',
      email: clerkUser.primaryEmailAddress?.emailAddress || clerkUser.emailAddresses?.[0]?.emailAddress || '',
      profileImageUrl: clerkUser.imageUrl,
      lastActive: serverTimestamp()
    }

    if (userSnap.exists()) {
      // Update existing user
      await updateDoc(userRef, profileData)
    } else {
      // Create new user
      await setDoc(userRef, {
        id: clerkUser.id,
        ...profileData,
        createdAt: serverTimestamp(),
        spottedTeams: [],
        totalSpots: 0,
        achievements: []
      })
    }
  } catch (error) {
    console.error('Error syncing user profile:', error)
  }
}
